import { InvoiceCustomizationPanel } from "@components/invoice-customization/components/InvoiceCustomizationPanel";
import type { InvoiceCustomStyleController } from "@components/invoice-customization/hooks/useInvoiceCustomStyle";
import { useInvoiceCustomStyle } from "@components/invoice-customization/hooks/useInvoiceCustomStyle";
import type { InvoiceCustomStyle } from "@components/invoice-customization/types/invoiceCustomStyle.types";
import { DEFAULT_INVOICE_SEQUENTIAL_START, INVOICE_NUMBERING_MODES } from "@components/invoice-numbering/types/invoiceNumbering.types";
import { normalizeInvoiceNumberingState } from "@components/invoice-numbering/utils/normalizeInvoiceNumbering";
import { useCompanyInfo } from "@components/shared/hooks/useCompany";
import { Box, Button, Container, Grid, Step, StepButton, Stepper, useMediaQuery } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { RootState } from "../../../redux/store";
import { formatHHMM, formatStatus, getJobsById, getRecordEmail } from "../../../utils";
import { sortJobEventsByStartDate, type JobEventForInvoice } from "../../jobs/jobs-details/events/board/context";
import { useJobsByIdQuery } from "../../jobs/jobs-list/jobs-layout/hooks/useJobByIdQuery";
import { useQuoteDepositAvailable } from "../../jobs2/JobCreate/hooks/useQuoteDepositAvailable";
import { ListItemTable } from "../../scope-of-work/components/ListTable";
import { roundMoney } from "../../scope-of-work/components/ListTable/components/calculateTotal";
import { useGetScopeOfWork } from "../../scope-of-work/hooks/useGetScopeOfWork";
import { colors } from "../../ui/colors";
import { useDialog } from "../../ui/dialogs/useDialog";
import { filledButtonSecondary } from "../../ui/styles/buttonStyles";
import { ClientInfoCard, useClientInfo } from "./ClientInfo";
import { ClientPreview } from "./ClientPreview";
import { InvoiceBilledVisitsCard, type InvoiceBilledVisitRow } from "./components/InvoiceBilledVisitsCard";
import { type InvoiceDiscountApplyToOption, type InvoiceDiscountDraft } from "./components/InvoiceDiscountsCard";
import { InvoiceExtraLineItemsCard, type InvoiceExtraLineItem } from "./components/InvoiceExtraLineItemsCard";
import { InvoiceHeaderClientView } from "./components/InvoiceHeaderClientView";
import { InvoiceMultiJobTotalsCard } from "./components/InvoiceMultiJobTotalsCard";
import { InvoicePerJobLineItemsCard, type InvoicePerJobLineGroup } from "./components/InvoicePerJobLineItemsCard";
import { InvoiceRelatedInvoicesCard } from "./components/InvoiceRelatedInvoicesCard";
import type { InvoiceServiceFeeDraft } from "./components/InvoiceServiceFeesCard";
import { JobTotalFallbackCard } from "./components/JobTotalFallbackCard";
import { CC_FEE_ID, PaymentProcessingFeeCard } from "./components/PaymentProcessingFeeCard";
import { CreateInvoiceProvider } from "./context/CreateInvoiceProvider";
import { useCreateInvoiceContext } from "./context/hooks/useCreateContext";
import { EmailRecipient, EmailRecipientsList, useEmailRecipients } from "./EmailRecipients";
import { useCreateInvoice } from "./hooks/useCreateInvoice";
import { useCompanyPaymentsStatusQuery } from "./hooks/usePayments";
import { ECurrency } from "./interfaces/invoice.interfaces";
import { NotesAndTermsInput, useNotesAndTerms } from "./NotesAndTerms";
import { PaymentMethodSelector, usePaymentMethodState, useSetPaymentMethod, type PaymentMethodState } from "./PaymentMethods";
import { InvoiceCalculations } from "./shared/types";
import {
  amountByKeyAfterItemDiscounts,
  computeDraftAdjustmentAmounts,
  extraLineItemApplyToKey,
  resolveApplyToBaseAmount,
  sanitizeAmountDraft,
} from "./shared/utils";
import type { InvoiceVisitGroupSelection } from "./types/invoiceVisitGroups";
import { buildClientPreviewLineItems } from "./utils/buildClientPreviewLineItems";
import { mapInvoiceDataToBackend } from "./utils/mapInvoiceData";
import { getFallbackInvoiceTotalFromJob } from "./utils/quoteJobInvoiceTotals";
import { ViewModeToggle } from "./ViewModeToggle";

interface Props {
  time?: string;
  jobUid: number;
  jobEvents: JobEventForInvoice[];
  clientUid?: string;
  scopeOfWorkUid?: string;
  eventSchedulerUid: string;
  invoiceModal: string;
  visitGroups?: InvoiceVisitGroupSelection[];
}

export const InvoiceCreate = ({ time, jobUid, jobEvents, clientUid, scopeOfWorkUid, eventSchedulerUid, invoiceModal, visitGroups }: Props) => {
  const [clientViewMode, setClientViewMode] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const invoiceStyleController = useInvoiceCustomStyle();
  const { value: invoiceCustomStyle, setValue: setInvoiceCustomStyle } = invoiceStyleController;
  const { client, isLoading, isError } = useClientInfo(clientUid);
  const { data: companyInfo } = useCompanyInfo();
  const reduxCompany = useSelector((state: RootState) => state.userState.company);
  const company = companyInfo ?? reduxCompany;
  const { data: paymentAvailableData, isLoading: paymentLoading } = useCompanyPaymentsStatusQuery(company?.id);

  const { data: scopeOfWork, isLoading: isLoadingScopeOfWork } = useGetScopeOfWork({ scopeOfWorkUid });
  const clientEmail = useMemo(() => getRecordEmail(client), [client]);
  const paymentMethod = usePaymentMethodState();

  const { reset: resetPaymentMethod } = useSetPaymentMethod();

  useEffect(() => {
    resetPaymentMethod();
  }, [resetPaymentMethod]);

  const hasClientPaymentMethods = paymentMethod.selectedPayments.length > 0 && !paymentMethod.markAsPaid;

  const initialEmailRecipients = useMemo<EmailRecipient[]>(() => {
    if (clientEmail && !isLoading) {
      return [
        {
          id: "1",
          email: clientEmail,
          permissionLevel: "Can Pay",
          isLocked: true,
        },
      ];
    }
    return [];
  }, [clientEmail, isLoading]);

  const { emailRecipients, addRecipient, deleteRecipient, updateRecipient, setAllPermissionLevels } = useEmailRecipients(
    initialEmailRecipients,
    "Can Pay",
  );

  const { notes, updateNotes } = useNotesAndTerms();

  const [allowPartialPayments, setAllowPartialPayments] = useState(false);
  const [minimumPaymentAmount, setMinimumPaymentAmount] = useState<string>("");

  useEffect(() => {
    if (allowPartialPayments) {
      const companyMinVal = company?.minimumPaymentAmount;
      if (companyMinVal != null && Number(companyMinVal) > 0) {
        setMinimumPaymentAmount((prev) => prev || String(companyMinVal));
      }
    }
  }, [allowPartialPayments, company]);

  return (
    <CreateInvoiceProvider>
      <InvoiceCreateContent
        company={company}
        time={time}
        jobUid={jobUid}
        jobEvents={jobEvents}
        clientUid={clientUid}
        scopeOfWorkUid={scopeOfWorkUid}
        eventSchedulerUid={eventSchedulerUid}
        client={client}
        isLoading={isLoading}
        scopeOfWork={scopeOfWork}
        isLoadingScopeOfWork={isLoadingScopeOfWork}
        initialEmailRecipients={initialEmailRecipients}
        emailRecipients={emailRecipients}
        addRecipient={addRecipient}
        deleteRecipient={deleteRecipient}
        updateRecipient={updateRecipient}
        paymentMethod={paymentMethod}
        notes={notes}
        updateNotes={updateNotes}
        paymentAvailableData={paymentAvailableData}
        sendEmail={sendEmail}
        setSendEmail={setSendEmail}
        clientViewMode={clientViewMode}
        setClientViewMode={setClientViewMode}
        invoiceModal={invoiceModal}
        visitGroups={visitGroups}
        allowPartialPayments={allowPartialPayments}
        setAllowPartialPayments={setAllowPartialPayments}
        minimumPaymentAmount={minimumPaymentAmount}
        setMinimumPaymentAmount={setMinimumPaymentAmount}
        invoiceCustomStyle={invoiceCustomStyle}
        setInvoiceCustomStyle={setInvoiceCustomStyle}
        invoiceStyleController={invoiceStyleController}
      />
    </CreateInvoiceProvider>
  );
};

const generateIdentifierId = (jobUid: number, time?: string | null, company?: any): string => {
  const numberingState = normalizeInvoiceNumberingState(company);

  if (numberingState.invoiceNumberingMode === INVOICE_NUMBERING_MODES.SEQUENTIAL) {
    const nextNumber = numberingState.invoiceNextNumber ?? DEFAULT_INVOICE_SEQUENTIAL_START;
    return String(nextNumber);
  }

  let dateString = new Date().toISOString().replace("T", "-").substring(0, 16).replace(":", "");

  if (time && time !== "null" && time !== "undefined") {
    try {
      const parsedDate = new Date(time);
      if (!isNaN(parsedDate.getTime())) {
        const year = parsedDate.getUTCFullYear();
        const month = String(parsedDate.getUTCMonth() + 1).padStart(2, "0");
        const day = String(parsedDate.getUTCDate()).padStart(2, "0");
        const hour = String(parsedDate.getUTCHours()).padStart(2, "0");
        const min = String(parsedDate.getUTCMinutes()).padStart(2, "0");
        dateString = `${year}-${month}-${day}-${hour}${min}`;
      }
    } catch (error) {
      console.warn("Failed to parse date for identifierId:", time);
    }
  }

  return `INV-${jobUid}-${dateString}`;
};

const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  let parent = element?.parentElement ?? null;

  while (parent) {
    const styles = window.getComputedStyle(parent);
    const overflowY = styles.overflowY;

    if ((overflowY === "auto" || overflowY === "scroll") && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
};

export const InvoiceCreateContent = ({
  invoiceCustomStyle,
  setInvoiceCustomStyle,
  invoiceStyleController,
  company,
  time,
  jobUid,
  jobEvents,
  scopeOfWorkUid,
  eventSchedulerUid,
  client,
  isLoading,
  scopeOfWork,
  emailRecipients,
  addRecipient,
  deleteRecipient,
  updateRecipient,
  paymentMethod,
  notes,
  updateNotes,
  paymentAvailableData,
  sendEmail,
  setSendEmail,
  clientViewMode,
  setClientViewMode,
  invoiceModal,
  visitGroups,
  allowPartialPayments,
  setAllowPartialPayments,
  minimumPaymentAmount,
  setMinimumPaymentAmount,
}: {
  company: RootState["userState"]["company"];
  time?: string;
  jobUid: number;
  jobEvents: JobEventForInvoice[];
  clientUid?: string;
  scopeOfWorkUid?: string;
  eventSchedulerUid: string;
  client: any;
  isLoading: boolean;
  scopeOfWork: any;
  isLoadingScopeOfWork: boolean;
  initialEmailRecipients: EmailRecipient[];
  emailRecipients: EmailRecipient[];
  addRecipient: () => void;
  deleteRecipient: (id: string) => void;
  updateRecipient: (id: string, field: keyof EmailRecipient, value: any) => void;
  paymentMethod: PaymentMethodState;
  notes: string | null;
  updateNotes: (notes: string | null) => void;
  paymentAvailableData: any;
  sendEmail: boolean;
  setSendEmail: (value: boolean) => void;
  clientViewMode: boolean;
  setClientViewMode: (value: boolean) => void;
  invoiceModal: string;
  visitGroups?: InvoiceVisitGroupSelection[];
  allowPartialPayments: boolean;
  setAllowPartialPayments: (value: boolean) => void;
  minimumPaymentAmount: string;
  setMinimumPaymentAmount: (value: string) => void;
  invoiceCustomStyle: InvoiceCustomStyle;
  setInvoiceCustomStyle: React.Dispatch<React.SetStateAction<InvoiceCustomStyle>>;
  invoiceStyleController: InvoiceCustomStyleController;
}) => {
  const { state, controller } = useCreateInvoiceContext();
  const { createInvoice, isPending } = useCreateInvoice();

  const globalFeeLabel =
    (company as any)?.invoiceCustomStyle?.labels?.increasePercentageByFrequency || invoiceCustomStyle?.labels?.increasePercentageByFrequency;

  useEffect(() => {
    if (
      globalFeeLabel &&
      (!state.serviceValues.serviceFeeLabel ||
        state.serviceValues.serviceFeeLabel === "Service fee" ||
        state.serviceValues.serviceFeeLabel === "Service Fee")
    ) {
      controller.setServiceValues({ serviceFeeLabel: globalFeeLabel });
    }
  }, [globalFeeLabel, state.serviceValues.serviceFeeLabel, controller]);
  const { showSnackbar } = useGlobalSnackbar();
  const { showDialog, DialogComponent } = useDialog();
  const { data: job, isLoading: isLoadingJob } = useJobsByIdQuery({ idStr: jobUid });
  const [activePreviewStep, setActivePreviewStep] = useState(0);

  const invoicePreviewRef = useRef<HTMLDivElement | null>(null);
  const emailPreviewRef = useRef<HTMLDivElement | null>(null);

  const handlePreviewStepChange = (step: number) => {
    setActivePreviewStep(step);

    const target = step === 0 ? invoicePreviewRef.current : emailPreviewRef.current;

    target?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };
  useEffect(() => {
    if (!clientViewMode) return;

    const invoiceElement = invoicePreviewRef.current;
    const emailElement = emailPreviewRef.current;

    if (!invoiceElement || !emailElement) return;

    const scrollContainer = getScrollParent(invoiceElement);

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visibleEntry) return;

        setActivePreviewStep(visibleEntry.target === emailElement ? 1 : 0);
      },
      {
        root: scrollContainer,
        threshold: [0.1, 0.25, 0.5, 0.75],
        rootMargin: "-15% 0px -45% 0px",
      },
    );

    observer.observe(invoiceElement);
    observer.observe(emailElement);

    return () => observer.disconnect();
  }, [clientViewMode]);

  const quoteIdCandidate =
    (job as { quoteId?: number | string | null; quote?: { id?: number | string | null } } | undefined)?.quoteId ??
    (job as { quote?: { id?: number | string | null } } | undefined)?.quote?.id ??
    null;

  const clientEmail = useMemo(() => getRecordEmail(client), [client]);
  const quoteIdNum = quoteIdCandidate != null && String(quoteIdCandidate).length > 0 ? Number(quoteIdCandidate) : null;
  const jobIdForDeposit = job?.id ?? jobUid;
  const depositAvailableQuery = useQuoteDepositAvailable(quoteIdNum ?? undefined, jobIdForDeposit);
  const [includedJobEvents, setIncludedJobEvents] = useState<JobEventForInvoice[]>(() => sortJobEventsByStartDate(jobEvents.map((e) => ({ ...e }))));

  const billedVisitRows = useMemo(() => {
    const eventsByUid = new Map<string, { startDate: string; endDate: string; status: string }>();
    (job?.events ?? []).forEach((e) => {
      eventsByUid.set(e.uid, { startDate: e.startDate, endDate: e.endDate, status: e.status });
    });
    return sortJobEventsByStartDate(includedJobEvents).map((row) => {
      const ev = eventsByUid.get(row.uid) ?? row;
      const start = ev.startDate;
      const startValid = Boolean(start && !Number.isNaN(new Date(start).getTime()));
      const title = startValid
        ? new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "2-digit" }).format(new Date(start))
        : "Visit";
      const subtitle =
        startValid && ev.endDate && !Number.isNaN(new Date(ev.endDate).getTime())
          ? `${formatHHMM(ev.startDate)} – ${formatHHMM(ev.endDate)} · ${formatStatus(ev.status)}`
          : `Event ${row.uid.slice(0, 8)}…`;
      return { uid: row.uid, title, subtitle };
    });
  }, [includedJobEvents, job?.events]);

  const billedVisitGroups = useMemo(() => {
    if (!visitGroups?.length) return undefined;
    const rowByUid = new Map(billedVisitRows.map((r) => [r.uid, r]));
    return visitGroups
      .map((g) => {
        const inGroup = includedJobEvents.filter((e) => g.eventUids.includes(e.uid));
        const sorted = sortJobEventsByStartDate(inGroup.map((e) => ({ ...e })));
        const rows = sorted.map((e) => rowByUid.get(e.uid)).filter((r): r is InvoiceBilledVisitRow => Boolean(r));
        return { jobId: g.jobId, jobLabel: g.jobLabel, rows };
      })
      .filter((g) => g.rows.length > 0);
  }, [visitGroups, billedVisitRows, includedJobEvents]);

  const availableVisits = useMemo(() => {
    if (billedVisitGroups?.length) {
      return billedVisitGroups.flatMap((group) =>
        group.rows.map((visit) => ({
          uid: visit.uid,
          label: group.jobLabel ? `${group.jobLabel} · ${visit.title}` : visit.title,
        })),
      );
    }
    return (billedVisitRows ?? []).map((visit) => ({
      uid: visit.uid,
      label: visit.title,
    }));
  }, [billedVisitGroups, billedVisitRows]);

  const uniqueInvoiceJobIds = useMemo(() => (visitGroups?.length ? Array.from(new Set(visitGroups.map((g) => g.jobId))) : []), [visitGroups]);

  const isMultiJobInvoice = uniqueInvoiceJobIds.length > 1;

  const invoiceJobsQueries = useQueries({
    queries: uniqueInvoiceJobIds.map((id) => ({
      queryKey: ["job", id],
      queryFn: async () => {
        const tok = sessionStorage.getItem("token");
        if (!tok) throw new Error("Missing auth token");
        const res = await getJobsById({ token: tok, id });
        return res?.data;
      },
      enabled: Boolean(id != null && isMultiJobInvoice),
      refetchOnWindowFocus: false,
    })),
  });

  const [perEventLineAmountStr, setPerEventLineAmountStr] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isMultiJobInvoice || !billedVisitGroups) return;
    setPerEventLineAmountStr((prev) => {
      const next = { ...prev };
      let changed = false;
      billedVisitGroups.forEach((group) => {
        const idx = uniqueInvoiceJobIds.indexOf(group.jobId);
        const j = idx !== -1 ? invoiceJobsQueries[idx]?.data : null;
        if (!j) return;
        const fb = getFallbackInvoiceTotalFromJob(j);
        if (fb > 0) {
          group.rows.forEach((visit) => {
            if (next[visit.uid] === undefined) {
              next[visit.uid] = String(fb);
              changed = true;
            }
          });
        }
      });
      return changed ? next : prev;
    });
  }, [isMultiJobInvoice, uniqueInvoiceJobIds, invoiceJobsQueries, billedVisitGroups]);

  const perJobLineGroupsForCard = useMemo((): InvoicePerJobLineGroup[] | undefined => {
    if (!isMultiJobInvoice || !billedVisitGroups?.length) return undefined;
    return billedVisitGroups
      .filter((g): g is typeof g & { jobId: number } => typeof g.jobId === "number")
      .map((g) => ({ jobId: g.jobId, jobLabel: g.jobLabel, rows: g.rows }));
  }, [isMultiJobInvoice, billedVisitGroups]);

  const multiJobLineSubtotal = useMemo(() => {
    if (!isMultiJobInvoice || !perJobLineGroupsForCard) return null;
    let sum = 0;
    for (const group of perJobLineGroupsForCard) {
      for (const visit of group.rows) {
        const raw = perEventLineAmountStr[visit.uid];
        const n = Number.parseFloat(String(raw ?? "").replace(/,/g, ""));
        if (!Number.isFinite(n) || n <= 0) return null;
        sum += n;
      }
    }
    return sum > 0 ? sum : null;
  }, [isMultiJobInvoice, perJobLineGroupsForCard, perEventLineAmountStr]);

  const jobLinesLoading = isMultiJobInvoice && invoiceJobsQueries.some((q) => q.isLoading);
  const [multiJobGrandManual, setMultiJobGrandManual] = useState<number | null>(null);
  const [extraLineItems, setExtraLineItems] = useState<InvoiceExtraLineItem[]>([]);
  const [discounts, setDiscounts] = useState<InvoiceDiscountDraft[]>([]);
  const [serviceFees, setServiceFees] = useState<InvoiceServiceFeeDraft[]>([]);

  useEffect(() => {
    if (!isMultiJobInvoice) setMultiJobGrandManual(null);
  }, [isMultiJobInvoice]);

  useEffect(() => {
    if (!isMultiJobInvoice) return;
    setMultiJobGrandManual(null);
  }, [isMultiJobInvoice, perEventLineAmountStr]);

  const normalizedExtraLineItems = useMemo(
    () =>
      extraLineItems
        .map((item) => {
          const totalAmount = roundMoney(Number.parseFloat(String(item.amount || "0")) || 0);
          const unitPrice = item.unitPrice ? roundMoney(Number.parseFloat(item.unitPrice) || 0) : undefined;

          let allocations = (item.allocations ?? [])
            .map((a) => {
              const parsed = roundMoney(Number.parseFloat(String(a.amount || "0")) || 0);
              return {
                eventUid: a.eventUid,
                amount: parsed,
              };
            })
            .filter((a) => a.eventUid && a.amount >= 0);

          if (allocations.length === 0 && availableVisits.length === 1 && totalAmount > 0) {
            allocations = [{ eventUid: availableVisits[0].uid, amount: totalAmount }];
          }

          return {
            id: item.id,
            title: item.title.trim(),
            amount: totalAmount,
            unitPrice,
            description: item.description?.trim() || undefined,
            allocations: allocations.length > 0 ? allocations : undefined,
          };
        })
        .filter((item) => item.title.length > 0 && item.amount > 0),
    [extraLineItems, availableVisits],
  );

  const extraLineItemsTotal = useMemo(
    () => roundMoney(normalizedExtraLineItems.reduce((acc, item) => acc + item.amount, 0)),
    [normalizedExtraLineItems],
  );

  const discountApplyToOptions = useMemo((): InvoiceDiscountApplyToOption[] => {
    const options: InvoiceDiscountApplyToOption[] = [{ key: "invoice", label: "Entire invoice", type: "invoice" }];
    (billedVisitGroups ?? []).forEach((group) => {
      group.rows.forEach((visit) => {
        options.push({
          key: visit.uid,
          label: group.jobLabel ? `${group.jobLabel} · ${visit.title}` : visit.title,
          type: "service",
          jobId: typeof group.jobId === "number" ? group.jobId : undefined,
          eventUid: visit.uid,
        });
      });
    });
    extraLineItems.forEach((item, index) => {
      options.push({
        key: extraLineItemApplyToKey(item.id),
        label: item.title.trim() || `Extra line item ${index + 1}`,
        type: "extra_line_item",
        extraLineItemId: item.id,
      });
    });
    return options;
  }, [billedVisitGroups, extraLineItems]);

  const applyToAmountByKey = useMemo(() => {
    const map: Record<string, number> = {};
    (billedVisitGroups ?? []).forEach((group) => {
      group.rows.forEach((visit) => {
        const raw = perEventLineAmountStr[visit.uid];
        const fromMulti = roundMoney(Number.parseFloat(String(raw ?? "").replace(/,/g, "")) || 0);
        if (fromMulti > 0) map[visit.uid] = fromMulti;
      });
    });
    (state.serviceValues.serviceLines ?? []).forEach((line) => {
      if (!line?.eventUid) return;
      const amount = roundMoney(Number(line.lineTotal ?? 0));
      if (!(map[line.eventUid] > 0) && amount > 0) map[line.eventUid] = amount;
    });
    extraLineItems.forEach((item) => {
      map[extraLineItemApplyToKey(item.id)] = roundMoney(Number.parseFloat(String(item.amount || "0")) || 0);
    });
    return map;
  }, [billedVisitGroups, perEventLineAmountStr, state.serviceValues.serviceLines, extraLineItems]);

  const handleRemoveVisitFromInvoice = (uid: string) => {
    setIncludedJobEvents((prev) => (prev.length <= 1 ? prev : prev.filter((e) => e.uid !== uid)));
    setDiscounts((prev) => prev.filter((item) => item.appliesToKey !== uid));
    setServiceFees((prev) => prev.filter((item) => item.appliesToKey !== uid));
  };

  const handleRemoveExtraLineItem = (id: string) => {
    const applyKey = extraLineItemApplyToKey(id);
    setExtraLineItems((prev) => prev.filter((item) => item.id !== id));
    setDiscounts((prev) => prev.filter((item) => item.appliesToKey !== applyKey));
    setServiceFees((prev) => prev.filter((item) => item.appliesToKey !== applyKey));
  };

  const calculations = useMemo<InvoiceCalculations>(() => {
    const totalFromTable = state.serviceValues.subTotal ?? 0;
    const currentFeeLabel = state.serviceValues.serviceFeeLabel || invoiceCustomStyle?.labels?.increasePercentageByFrequency || "Service fee";

    const sv = state.serviceValues;
    const pricingDetail =
      typeof sv.computedGrandTotal === "number" && sv.time != null
        ? {
            serviceLine: {
              qty: roundMoney(sv.time / 60),
              unitPrice: roundMoney(sv.effectiveLineUnitPrice ?? 0),
              lineTotal:
                sv.isServiceLineTotalOverridden && sv.serviceTotal != null ? roundMoney(sv.serviceTotal) : roundMoney(sv.computedLineTotal ?? 0),
              isLineTotalOverridden: sv.isServiceLineTotalOverridden === true,
            },
            isGlobalTotalOverridden: sv.isGlobalTotalOverridden === true,
            computedGlobalTotal: roundMoney(sv.computedGrandTotal),
            serviceFeeLabel: currentFeeLabel,
            extraLineItems: normalizedExtraLineItems,
          }
        : undefined;

    return {
      subtotal: totalFromTable,
      serviceFeeAmount: 0,
      serviceFeeLabel: currentFeeLabel,
      total: totalFromTable,
      pricingDetail,
    };
  }, [state.serviceValues, invoiceCustomStyle?.labels?.increasePercentageByFrequency, normalizedExtraLineItems]);

  const fallbackLineTotal = useMemo(() => getFallbackInvoiceTotalFromJob(job), [job]);

  const singleJobEffectiveCalculations = useMemo<InvoiceCalculations>(() => {
    const base = calculations.total ?? 0;
    if (base > 0) return calculations;
    const fb = fallbackLineTotal;
    if (fb > 0) {
      return {
        ...calculations,
        subtotal: fb,
        total: fb,
      };
    }
    return calculations;
  }, [calculations, fallbackLineTotal]);

  const serviceLineSubtotal = isMultiJobInvoice
    ? (multiJobLineSubtotal ?? 0)
    : roundMoney(singleJobEffectiveCalculations.subtotal ?? singleJobEffectiveCalculations.total ?? 0);

  const resolvedApplyToAmountByKey = useMemo(() => {
    const map = { ...applyToAmountByKey };
    const visitUids = (billedVisitGroups ?? []).flatMap((group) => group.rows.map((row) => row.uid));
    if (visitUids.length === 1 && !(map[visitUids[0]] > 0)) {
      map[visitUids[0]] = serviceLineSubtotal;
    }
    return map;
  }, [applyToAmountByKey, billedVisitGroups, serviceLineSubtotal]);

  const merchandiseSubtotal = roundMoney(serviceLineSubtotal + extraLineItemsTotal);

  const discountComputedAmounts = useMemo(
    () => computeDraftAdjustmentAmounts(discounts, merchandiseSubtotal, resolvedApplyToAmountByKey),
    [discounts, merchandiseSubtotal, resolvedApplyToAmountByKey],
  );

  const discountsTotalPreview = useMemo(
    () => roundMoney(Object.values(discountComputedAmounts).reduce((acc, amount) => acc + amount, 0)),
    [discountComputedAmounts],
  );

  const amountByKeyAfterDiscounts = useMemo(
    () => amountByKeyAfterItemDiscounts(resolvedApplyToAmountByKey, discounts, discountComputedAmounts),
    [resolvedApplyToAmountByKey, discounts, discountComputedAmounts],
  );

  const serviceFeeComputedAmounts = useMemo(() => {
    const invoiceBaseAfterDiscounts = roundMoney(Math.max(0, merchandiseSubtotal - discountsTotalPreview));
    const map: Record<string, number> = {};
    serviceFees.forEach((item) => {
      const value = Number.parseFloat(String(item.value || "0")) || 0;
      const base = resolveApplyToBaseAmount(item.appliesToType, item.appliesToKey, invoiceBaseAfterDiscounts, amountByKeyAfterDiscounts);
      map[item.id] =
        item.kind === "percentage" ? roundMoney((Math.max(0, base) * Math.min(Math.max(0, value), 100)) / 100) : roundMoney(Math.max(0, value));
    });
    return map;
  }, [serviceFees, amountByKeyAfterDiscounts, merchandiseSubtotal, discountsTotalPreview]);

  const serviceFeesTotal = useMemo(
    () => roundMoney(Object.values(serviceFeeComputedAmounts).reduce((acc, amount) => acc + amount, 0)),
    [serviceFeeComputedAmounts],
  );

  const normalizedServiceFees = useMemo(() => {
    return serviceFees
      .map((item) => {
        const amount = roundMoney(serviceFeeComputedAmounts[item.id] ?? 0);
        const label = item.label.trim();
        const value = Number.parseFloat(String(item.value || "0")) || 0;
        if (!label || !(amount > 0)) return null;
        const option = discountApplyToOptions.find((opt) => opt.key === (item.appliesToKey || "invoice"));
        const feeType = item.feeType || (item.id === "credit-card-processing-fee" ? "credit_card_surcharge" : "other");
        return {
          id: item.id,
          feeType,
          alias: label,
          kind: item.kind,
          value,
          quantity: { value: 1, unit: "per_item" },
          price: { value: amount, by: "unique", currency: "USD" },
          total: { value: amount, by: "unique", currency: "USD" },
          appliesTo: option
            ? {
                type: option.type === "extra_line_item" ? ("invoice" as const) : option.type,
                ...(option.jobId != null ? { jobId: option.jobId } : {}),
                ...(option.eventUid ? { eventUid: option.eventUid } : {}),
                label: option.label,
              }
            : { type: "invoice" as const },
        };
      })
      .filter((item): item is NonNullable<typeof item> => item != null);
  }, [serviceFees, serviceFeeComputedAmounts, discountApplyToOptions]);

  const effectiveCalculations = useMemo<InvoiceCalculations>(() => {
    const feeLabel =
      normalizedServiceFees[0]?.alias ||
      singleJobEffectiveCalculations.serviceFeeLabel ||
      state.serviceValues.serviceFeeLabel ||
      invoiceCustomStyle?.labels?.increasePercentageByFrequency ||
      "Service fee";

    const feeAmount = serviceFeesTotal;
    const serviceFeesPayload = normalizedServiceFees;

    const attachAdjustments = (merchandiseTotal: number, pricingDetail: InvoiceCalculations["pricingDetail"]) => {
      const computedAmounts: Record<string, number> = {};
      const normalizedDiscounts = discounts
        .map((item) => {
          const value = Number.parseFloat(String(item.value || "0")) || 0;
          const option = discountApplyToOptions.find((opt) => opt.key === (item.appliesToKey || "invoice"));
          const amount = roundMoney(discountComputedAmounts[item.id] ?? 0);
          computedAmounts[item.id] = amount;
          const description = item.description.trim();
          if (!description || !(amount > 0)) return null;
          return {
            description,
            kind: item.kind,
            value,
            amount,
            appliesTo: option
              ? {
                  type: option.type,
                  ...(option.jobId != null ? { jobId: option.jobId } : {}),
                  ...(option.eventUid ? { eventUid: option.eventUid } : {}),
                  ...(option.extraLineItemId ? { extraLineItemId: option.extraLineItemId } : {}),
                  label: option.label,
                }
              : { type: "invoice" as const },
          };
        })
        .filter((item): item is NonNullable<typeof item> => item != null);
      const discountsTotal = roundMoney(normalizedDiscounts.reduce((acc, item) => acc + item.amount, 0));
      const total = roundMoney(Math.max(0, merchandiseTotal - discountsTotal + feeAmount));
      return {
        computedAmounts,
        discounts: normalizedDiscounts,
        discountsTotal,
        total,
        pricingDetail: pricingDetail
          ? {
              ...pricingDetail,
              serviceFeeLabel: feeLabel,
              serviceTypeName: scopeOfWork?.typeOfService?.name || scopeOfWork?.name || undefined,
              extraLineItems: normalizedExtraLineItems,
              serviceFees: serviceFeesPayload,
              discounts: normalizedDiscounts,
              computedGlobalTotal: total,
            }
          : undefined,
      };
    };

    if (!isMultiJobInvoice) {
      const merchandiseTotal = roundMoney((singleJobEffectiveCalculations.total ?? 0) + extraLineItemsTotal);
      const adjusted = attachAdjustments(merchandiseTotal, singleJobEffectiveCalculations.pricingDetail);
      return {
        ...singleJobEffectiveCalculations,
        serviceFeeAmount: feeAmount,
        serviceFeeLabel: feeLabel,
        serviceFees: serviceFeesPayload,
        extraLineItemsTotal,
        extraLineItems: normalizedExtraLineItems,
        discounts: adjusted.discounts,
        discountsTotal: adjusted.discountsTotal,
        total: adjusted.total,
        pricingDetail: adjusted.pricingDetail
          ? {
              ...adjusted.pricingDetail,
              serviceLines: state.serviceValues.serviceLines?.map((sl) => ({
                jobId: jobUid,
                qty: sl.qty,
                unitPrice: sl.unitPrice,
                lineTotal: sl.lineTotal,
                isLineTotalOverridden: sl.isLineTotalOverridden,
                eventUid: sl.eventUid,
              })),
            }
          : {
              serviceLine: {
                qty: 1,
                unitPrice: roundMoney(singleJobEffectiveCalculations.subtotal ?? 0),
                lineTotal: roundMoney(singleJobEffectiveCalculations.subtotal ?? 0),
                isLineTotalOverridden: false,
              },
              isGlobalTotalOverridden: false,
              computedGlobalTotal: adjusted.total,
              serviceFeeLabel: feeLabel,
              extraLineItems: normalizedExtraLineItems,
              serviceFees: serviceFeesPayload,
              discounts: adjusted.discounts,
            },
      };
    }

    const sub = multiJobLineSubtotal ?? 0;
    const merchandiseTotal = roundMoney(sub + extraLineItemsTotal);

    const serviceLines: Array<{
      jobId: number;
      eventUid?: string;
      qty: number;
      unitPrice: number;
      lineTotal: number;
      isLineTotalOverridden: boolean;
    }> = [];

    if (perJobLineGroupsForCard) {
      for (const group of perJobLineGroupsForCard) {
        for (const visit of group.rows) {
          const raw = perEventLineAmountStr[visit.uid];
          const amount = roundMoney(Number.parseFloat(String(raw ?? "").replace(/,/g, "")) || 0);
          serviceLines.push({
            jobId: group.jobId,
            eventUid: visit.uid,
            qty: 1,
            unitPrice: amount,
            lineTotal: amount,
            isLineTotalOverridden: false,
          });
        }
      }
    }

    const pricingDetail = {
      serviceLine: {
        qty: 1,
        unitPrice: roundMoney(sub),
        lineTotal: roundMoney(sub),
        isLineTotalOverridden: false,
      },
      isGlobalTotalOverridden: multiJobGrandManual != null,
      computedGlobalTotal: roundMoney(merchandiseTotal + feeAmount),
      serviceFeeLabel: feeLabel,
      extraLineItems: normalizedExtraLineItems,
      serviceLines,
      serviceFees: serviceFeesPayload,
    };
    const adjusted = attachAdjustments(merchandiseTotal, pricingDetail);
    const total = multiJobGrandManual != null && Number.isFinite(multiJobGrandManual) ? roundMoney(multiJobGrandManual) : adjusted.total;

    return {
      subtotal: multiJobLineSubtotal != null ? multiJobLineSubtotal : 0,
      serviceFeeAmount: feeAmount,
      serviceFeeLabel: feeLabel,
      serviceFees: serviceFeesPayload,
      extraLineItemsTotal,
      extraLineItems: normalizedExtraLineItems,
      discounts: adjusted.discounts,
      discountsTotal: adjusted.discountsTotal,
      total,
      pricingDetail: adjusted.pricingDetail
        ? {
            ...adjusted.pricingDetail,
            computedGlobalTotal: total,
          }
        : undefined,
    };
  }, [
    isMultiJobInvoice,
    multiJobLineSubtotal,
    multiJobGrandManual,
    singleJobEffectiveCalculations,
    extraLineItemsTotal,
    normalizedExtraLineItems,
    state.serviceValues.serviceFeeLabel,
    perEventLineAmountStr,
    perJobLineGroupsForCard,
    jobUid,
    state.serviceValues.serviceLines,
    discounts,
    discountApplyToOptions,
    discountComputedAmounts,
    serviceFeesTotal,
    normalizedServiceFees,
    invoiceCustomStyle?.labels?.increasePercentageByFrequency,
  ]);

  const multiJobComputedGrand = useMemo(() => {
    if (!isMultiJobInvoice) return 0;
    return roundMoney((multiJobLineSubtotal ?? 0) + extraLineItemsTotal - discountsTotalPreview + serviceFeesTotal);
  }, [isMultiJobInvoice, multiJobLineSubtotal, extraLineItemsTotal, discountsTotalPreview, serviceFeesTotal]);

  const effectiveDepositAvailable = useMemo(() => {
    const uid = depositAvailableQuery.data?.depositInvoiceUid;
    if (!uid) return undefined;
    return Math.max(0, Number(depositAvailableQuery.data?.available ?? 0));
  }, [depositAvailableQuery.data]);

  const depositCredit = useMemo(() => {
    const d = depositAvailableQuery.data;
    if (!d?.depositInvoiceUid || effectiveDepositAvailable == null) return null;
    const pool = Math.max(0, Number(effectiveDepositAvailable) || 0);
    const lineTotal = Number(effectiveCalculations.total ?? 0);
    const fallbackCap = isMultiJobInvoice && multiJobLineSubtotal != null ? Number(multiJobLineSubtotal) : Number(fallbackLineTotal ?? 0);
    const cap = lineTotal > 0 ? lineTotal : fallbackCap > 0 ? fallbackCap : 0;
    if (cap <= 0 || pool <= 0) return null;
    const credited = Math.min(pool, cap);
    if (credited <= 0) return null;
    return { parentDepositInvoiceUid: d.depositInvoiceUid, creditedDepositAmount: credited };
  }, [depositAvailableQuery.data, effectiveCalculations, effectiveDepositAvailable, fallbackLineTotal, isMultiJobInvoice, multiJobLineSubtotal]);

  const invoicePreviewDetails = useMemo(
    () => ({
      uid: "preview",
      identifierId: generateIdentifierId(jobUid, time, company),
      createdAt: new Date().toISOString(),
      type: "standard",

      company,
      emailRecipients,

      billingJobEvents: includedJobEvents.map((event) => ({
        ...event,
        job: {
          ...job,
          company,
        },
      })),

      mainAreasCost: {
        total: {
          value: effectiveCalculations.subtotal ?? 0,
          currency: "USD",
        },
      },

      increasePercentageByFrequency: {
        total: {
          value: effectiveCalculations.serviceFeeAmount ?? 0,
          currency: "USD",
        },
      },

      totalCost: {
        total: {
          value: effectiveCalculations.total ?? 0,
          currency: "USD",
        },
      },

      pricingDetail: effectiveCalculations.pricingDetail,

      notes,

      creditedDepositAmount: depositCredit?.creditedDepositAmount ?? 0,
    }),
    [jobUid, time, company, emailRecipients, includedJobEvents, job, effectiveCalculations, notes, depositCredit],
  );

  const hasIncompleteServiceFee = useMemo(
    () =>
      serviceFees.some((item) => {
        const label = item.label.trim();
        const value = Number.parseFloat(String(item.value || "0"));
        return label.length === 0 || !(Number.isFinite(value) && value >= 0);
      }),
    [serviceFees],
  );

  const hasIncompleteDiscount = useMemo(
    () =>
      discounts.some((item) => {
        const description = item.description.trim();
        const value = Number.parseFloat(String(item.value || "0"));
        return description.length === 0 || !(Number.isFinite(value) && value >= 0);
      }),
    [discounts],
  );

  const hasIncompleteExtraLineItem = useMemo(
    () =>
      extraLineItems.some((item) => {
        const title = item.title.trim();
        const amount = Number.parseFloat(String(item.amount || "0"));
        const hasAmount = Number.isFinite(amount) && amount > 0;
        const hasAllocations = availableVisits.length <= 1 || (item.allocations && item.allocations.length > 0);
        return title.length === 0 || !hasAmount || !hasAllocations;
      }),
    [extraLineItems, availableVisits],
  );

  const hasIncompleteServiceLineAmount = useMemo(() => {
    if (!isMultiJobInvoice) return false;
    if (!perJobLineGroupsForCard?.length) return false;
    return perJobLineGroupsForCard.some((group) => {
      return group.rows.some((visit) => {
        const raw = perEventLineAmountStr[visit.uid] ?? "";
        const amount = Number.parseFloat(String(raw).replace(/,/g, ""));
        return !(Number.isFinite(amount) && amount > 0);
      });
    });
  }, [isMultiJobInvoice, perJobLineGroupsForCard, perEventLineAmountStr]);

  const handleGenerateInvoice = () => {
    if (isPending) return;

    try {
      if (!scopeOfWorkUid) {
        showSnackbar("Missing required data. Please ensure scope of work is available.", "error", 5000, "top-center");
        return;
      }

      if (includedJobEvents.length === 0) {
        showSnackbar("Select at least one visit to include on the invoice.", "warning", 5000, "top-center");
        return;
      }

      if (isMultiJobInvoice && (multiJobLineSubtotal == null || multiJobLineSubtotal <= 0)) {
        showSnackbar("Enter a valid service amount for each job before generating the invoice.", "warning", 5000, "top-center");
        return;
      }

      if (hasIncompleteServiceLineAmount) {
        showSnackbar("Complete each Service amount with a value greater than zero before generating the invoice.", "warning", 5000, "top-center");
        return;
      }

      if (hasIncompleteExtraLineItem) {
        showSnackbar("Complete each extra line item with title and amount greater than zero, or clear it.", "warning", 5000, "top-center");
        return;
      }

      if (hasIncompleteDiscount) {
        showSnackbar("Complete each discount with a description and a valid amount, or clear it.", "warning", 5000, "top-center");
        return;
      }

      if (hasIncompleteServiceFee) {
        showSnackbar("Complete each service fee with a label and a valid amount, or clear it.", "warning", 5000, "top-center");
        return;
      }

      if (sendEmail && (!emailRecipients || emailRecipients.length === 0)) {
        showSnackbar(
          "Please add at least one email recipient to send the invoice by email, or disable email delivery.",
          "warning",
          5000,
          "top-center",
        );
        return;
      }

      const hasClientPaymentMethods = paymentMethod.selectedPayments.length > 0 && !paymentMethod.markAsPaid;
      const hasCanPayRecipient = emailRecipients.some((recipient) => recipient.permissionLevel === "Can Pay");
      if (sendEmail && hasClientPaymentMethods && !hasCanPayRecipient) {
        showSnackbar("Select at least one recipient with Can Pay, or remove the payment methods.", "warning", 5000, "top-center");
        return;
      }

      const submitInvoice = () => {
        const identifierId = generateIdentifierId(jobUid, time, company);
        const companyName = company?.businessName?.trim() || company?.dba?.trim() || undefined;

        const invoicePayload = mapInvoiceDataToBackend(
          scopeOfWorkUid,
          sortJobEventsByStartDate(includedJobEvents),
          effectiveCalculations,
          {
            method: paymentMethod.method,
            selectedPayments: paymentMethod.selectedPayments,
            referenceNumber: paymentMethod.referenceNumber,
          },
          emailRecipients,
          notes,
          ECurrency.USD,
          sendEmail,
          paymentMethod.markAsPaid,
          paymentMethod.manualEntryNotes || undefined,
          paymentMethod.manualEntryTransactionId || undefined,
          identifierId,
          depositCredit,
          allowPartialPayments,
          minimumPaymentAmount ? Number.parseFloat(minimumPaymentAmount) : undefined,
          companyName,
        );

        createInvoice(invoicePayload, { invoiceModal });
      };

      if (!hasClientPaymentMethods) {
        showDialog({
          type: "confirm",
          title: "No payment method selected",
          message:
            "This invoice has no client payment method. Recipients will only be able to view it and will not receive a payment link. Do you want to continue?",
          confirmLabel: "Generate invoice",
          cancelLabel: "Cancel",
          onConfirm: submitInvoice,
          onCancel: () => {},
        });
        return;
      }

      submitInvoice();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An error occurred while preparing invoice data.";
      showSnackbar(errorMessage, "error", 5000, "top-center");
    }
  };

  const isXs = useMediaQuery("(max-width:600px)");
  const formattedIdentifier = generateIdentifierId(jobUid, time, company);

  const formattedIssuedDate = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
  });

  const hasClientPaymentMethods = paymentMethod.selectedPayments.length > 0 && !paymentMethod.markAsPaid;
  const hasCanPayRecipient = emailRecipients.some((recipient) => recipient.permissionLevel === "Can Pay");
  const needsPaymentMethodForCanPay = sendEmail && hasCanPayRecipient && !hasClientPaymentMethods;
  const isGenerateDisabled =
    isPending || !scopeOfWorkUid || hasIncompleteExtraLineItem || hasIncompleteServiceLineAmount || needsPaymentMethodForCanPay;

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 2, sm: 3 },
        pb: { xs: 5, sm: 6 },
        px: { xs: 2, sm: 3 },
        backgroundColor: colors.lightGray.base,
        position: "relative",
      }}
    >
      <ViewModeToggle isClientView={clientViewMode} onToggle={setClientViewMode} />

      {/* Email preview tab temporarily disabled: the invoice preview is the only mode for now.
      {clientViewMode ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={previewMode}
            onChange={(_, nextMode) => {
              if (nextMode) {
                setPreviewMode(nextMode);
              }
            }}
            sx={{
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              borderRadius: 2,
              p: 0.5,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              "& .MuiToggleButton-root": {
                border: 0,
                borderRadius: 1.5,
                px: 3,
                textTransform: "none",
                fontWeight: 700,
              },
            }}
          >
            <ToggleButton value="invoice">Invoice</ToggleButton>
            <ToggleButton value="email">Email</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ) : null}
      */}

      {/* Client / PDF Preview Mode */}
      <Box
        sx={{
          display: clientViewMode ? "grid" : "none",
          gridTemplateColumns: "320px minmax(0, 1fr) 110px",
          gap: 2,
          width: "100%",
          py: 2,
          bgcolor: colors.gray[50],
          borderRadius: 3,
          alignItems: "start",
        }}
      >
        {/* 1. Panel izquierdo */}
        <Box
          sx={{
            position: "sticky",
            top: 16,
            alignSelf: "start",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            overscrollBehavior: "contain",
            pr: 0.5,
          }}
        >
          <InvoiceCustomizationPanel
            value={invoiceCustomStyle}
            onChange={setInvoiceCustomStyle}
            isDirty={invoiceStyleController.isDirty}
            isSaving={invoiceStyleController.isSaving}
            onSave={invoiceStyleController.save}
            onDiscard={invoiceStyleController.reset}
          />
        </Box>

        {/* 2. PDF y Email centrados */}
        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
            width: "100%",
          }}
        >
          <Box
            ref={invoicePreviewRef}
            sx={{
              width: "100%",
              maxWidth: 700,
              minWidth: 600,
              mx: "auto",
              scrollMarginTop: 24,
            }}
          >
            <ClientPreview
              previewMode="invoice"
              invoiceCustomStyle={invoiceCustomStyle}
              invoiceDetails={invoicePreviewDetails}
              clientName={`${client?.firstName || ""} ${client?.lastName || ""}`}
              clientMail={clientEmail}
              lineItems={buildClientPreviewLineItems(scopeOfWork)}
              scopeOfWork={
                {
                  ...scopeOfWork,
                  data: effectiveCalculations,
                } as any
              }
              emailRecipients={emailRecipients}
              paymentMethod={paymentMethod.method}
              paymentType={paymentMethod.paymentType}
              referenceNumber={paymentMethod.referenceNumber}
              serviceFeeLabel={effectiveCalculations.serviceFeeLabel || "Service fee"}
              selectedPayments={paymentMethod.selectedPayments}
              identifierId={formattedIdentifier}
              issuedDate={new Date().toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
              onExit={() => setClientViewMode(false)}
            />
          </Box>

          <Box
            ref={emailPreviewRef}
            sx={{
              width: "100%",
              maxWidth: 700,
              mx: "auto",
              scrollMarginTop: 24,
            }}
          >
            <ClientPreview
              previewMode="email"
              invoiceCustomStyle={invoiceCustomStyle}
              invoiceDetails={invoicePreviewDetails}
              clientName={`${client?.firstName || ""} ${client?.lastName || ""}`}
              clientMail={clientEmail}
              lineItems={buildClientPreviewLineItems(scopeOfWork)}
              scopeOfWork={
                {
                  ...scopeOfWork,
                  data: effectiveCalculations,
                } as any
              }
              emailRecipients={emailRecipients}
              paymentMethod={paymentMethod.method}
              paymentType={paymentMethod.paymentType}
              referenceNumber={paymentMethod.referenceNumber}
              serviceFeeLabel={effectiveCalculations.serviceFeeLabel || "Service fee"}
              selectedPayments={paymentMethod.selectedPayments}
              identifierId={formattedIdentifier}
              issuedDate={new Date().toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
              onExit={() => setClientViewMode(false)}
            />
          </Box>
        </Box>

        {/* 3. Stepper derecho */}
        <Box
          sx={{
            position: "sticky",
            top: 16,
            alignSelf: "start",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              bgcolor: "background.paper",

              borderColor: "divider",
              borderRadius: 3,
              px: 1.5,
              py: 1.5,
              boxShadow: "0 10px 30px -10px rgba(0,0,0,0.08)",
              backdropFilter: "blur(8px)",
            }}
          >
            <Stepper
              activeStep={activePreviewStep}
              orientation="vertical"
              nonLinear
              sx={{
                "& .MuiStep-root": {
                  p: 0,
                },
                /* Conector más fino y sutil */
                "& .MuiStepConnector-line": {
                  minHeight: 16,
                  borderColor: "grey.200",
                  borderLeftWidth: 2,
                  ml: "2px",
                },
                /* Botón con border-radius y microinteracción al pasar el cursor */
                "& .MuiStepButton-root": {
                  py: 0.75,
                  px: 1,
                  borderRadius: 2,
                  justifyContent: "flex-start",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "rgba(251, 142, 0, 0.08)",
                  },
                },
                /* Estilos del texto/label */
                "& .MuiStepLabel-label": {
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "text.secondary",
                  transition: "color 0.2s ease",
                  whiteSpace: "nowrap",
                },
                "& .MuiStepLabel-label.Mui-active": {
                  color: "#FB8E00",
                  fontWeight: 800,
                },
                "& .MuiStepLabel-label.Mui-completed": {
                  color: "text.primary",
                  fontWeight: 600,
                },
                /* Personalización de los íconos (números/checkmarks) */
                "& .MuiStepIcon-root": {
                  color: "grey.300",
                  fontSize: "1.25rem",
                  transition: "color 0.2s ease, transform 0.2s ease",
                },
                "& .MuiStepIcon-root.Mui-active": {
                  color: "#FB8E00",
                  transform: "scale(1.1)",
                },
                "& .MuiStepIcon-root.Mui-completed": {
                  color: "#FB8E00",
                },
              }}
            >
              <Step>
                <StepButton onClick={() => handlePreviewStepChange(0)}>PDF</StepButton>
              </Step>

              <Step>
                <StepButton onClick={() => handlePreviewStepChange(1)}>Email</StepButton>
              </Step>
            </Stepper>
          </Box>
        </Box>
      </Box>

      {/* Invoice Editor Mode */}
      <Box sx={{ display: clientViewMode ? "none" : "block", width: "100%" }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Left Column: Form Details */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 3 } }}>
              <InvoiceHeaderClientView identifierId={formattedIdentifier} issuedDate={formattedIssuedDate} />

              <ClientInfoCard
                client={{
                  firstName: client?.firstName,
                  lastName: client?.lastName,
                  alias: client?.alias,
                  email: clientEmail,
                  phone: client?.phone,
                  avatar: client?.avatar,
                }}
                isLoading={isLoading}
              />

              <InvoiceBilledVisitsCard
                visits={billedVisitRows}
                visitGroups={billedVisitGroups}
                isLoading={isLoadingJob}
                onRemove={handleRemoveVisitFromInvoice}
                removeDisabled={includedJobEvents.length <= 1}
              />

              {!isMultiJobInvoice && visitGroups?.length === 1 && (billedVisitGroups?.[0]?.rows?.length ?? 0) > 1 && fallbackLineTotal > 0 ? (
                <JobTotalFallbackCard amount={fallbackLineTotal} />
              ) : null}

              {perJobLineGroupsForCard ? (
                <InvoicePerJobLineItemsCard
                  groups={perJobLineGroupsForCard}
                  lineAmountByEventUid={perEventLineAmountStr}
                  onLineAmountChange={(eventUid, raw) =>
                    setPerEventLineAmountStr((prev) => ({
                      ...prev,
                      [eventUid]: sanitizeAmountDraft(raw),
                    }))
                  }
                  isLoading={jobLinesLoading}
                  showValidation={hasIncompleteServiceLineAmount}
                  afterLineItems={
                    <InvoiceExtraLineItemsCard
                      variant="embedded"
                      items={extraLineItems}
                      availableVisits={availableVisits}
                      onAdd={() =>
                        setExtraLineItems((prev) => [
                          ...prev,
                          {
                            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                            title: "",
                            amount: "",
                            unitPrice: "",
                            allocations: availableVisits.length === 1 ? [{ eventUid: availableVisits[0].uid, amount: "" }] : [],
                          },
                        ])
                      }
                      onRemove={handleRemoveExtraLineItem}
                      onChange={(id, field, value) =>
                        setExtraLineItems((prev) =>
                          prev.map((item) =>
                            item.id === id
                              ? {
                                  ...item,
                                  [field]: field === "amount" ? sanitizeAmountDraft(value) : value,
                                }
                              : item,
                          ),
                        )
                      }
                      onChangeItem={(id, updated) => setExtraLineItems((prev) => prev.map((item) => (item.id === id ? updated : item)))}
                      totalAmount={extraLineItemsTotal}
                      showValidation={hasIncompleteExtraLineItem}
                    />
                  }
                  totalsSection={
                    isMultiJobInvoice ? (
                      <InvoiceMultiJobTotalsCard
                        variant="embedded"
                        lineItemsSubtotal={multiJobLineSubtotal}
                        serviceFeeAmount={effectiveCalculations.serviceFeeAmount ?? 0}
                        serviceFeeLabel="Service fees"
                        extraLineItemsAmount={extraLineItemsTotal}
                        discountsAmount={effectiveCalculations.discountsTotal ?? 0}
                        computedGrandTotal={multiJobComputedGrand}
                        displayTotal={effectiveCalculations.total ?? 0}
                        isTotalOverridden={multiJobGrandManual != null}
                        onTotalChange={(value) => {
                          if (value == null) return;
                          const rounded = roundMoney(value);
                          if (multiJobComputedGrand != null && rounded === roundMoney(multiJobComputedGrand)) {
                            setMultiJobGrandManual(null);
                          } else {
                            setMultiJobGrandManual(rounded);
                          }
                        }}
                        onTotalReset={() => setMultiJobGrandManual(null)}
                        serviceFees={serviceFees}
                        serviceFeeApplyToOptions={discountApplyToOptions}
                        serviceFeeComputedAmounts={serviceFeeComputedAmounts}
                        onAddServiceFee={(draft) =>
                          setServiceFees((prev) => [
                            ...prev,
                            draft
                              ? { ...draft, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` }
                              : {
                                  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                                  label: globalFeeLabel || "Service fee",
                                  kind: "amount",
                                  value: "",
                                  appliesToType: "invoice",
                                  appliesToKey: "invoice",
                                },
                          ])
                        }
                        onRemoveServiceFee={(id) => setServiceFees((prev) => prev.filter((item) => item.id !== id))}
                        onChangeServiceFee={(id, patch) =>
                          setServiceFees((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)))
                        }
                        showServiceFeeValidation={hasIncompleteServiceFee}
                        discounts={discounts}
                        discountApplyToOptions={discountApplyToOptions}
                        discountComputedAmounts={discountComputedAmounts}
                        onAddDiscount={(draft) =>
                          setDiscounts((prev) => [
                            ...prev,
                            draft
                              ? { ...draft, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` }
                              : {
                                  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                                  description: "",
                                  kind: "amount",
                                  value: "",
                                  appliesToType: "invoice",
                                  appliesToKey: "invoice",
                                },
                          ])
                        }
                        onRemoveDiscount={(id) => setDiscounts((prev) => prev.filter((item) => item.id !== id))}
                        onChangeDiscount={(id, patch) => setDiscounts((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)))}
                        showDiscountValidation={hasIncompleteDiscount}
                      />
                    ) : null
                  }
                />
              ) : null}

              <InvoiceRelatedInvoicesCard
                depositInvoiceUid={depositAvailableQuery.data?.depositInvoiceUid}
                scopeOfWorkUid={scopeOfWorkUid}
                depositAmount={depositAvailableQuery.data?.depositAmount}
                isLoading={depositAvailableQuery.isLoading}
              />

              {!isMultiJobInvoice ? (
                <ListItemTable
                  scopeOfWork={scopeOfWork}
                  showTotals={true}
                  eventSchedulerUid={eventSchedulerUid}
                  allowEdit={true}
                  invoiceDepositAvailable={effectiveDepositAvailable}
                  invoiceDepositInvoiceUid={depositAvailableQuery.data?.depositInvoiceUid ?? null}
                  invoiceDepositCreditApplied={depositCredit?.creditedDepositAmount ?? null}
                  invoiceLineGrossFallback={getFallbackInvoiceTotalFromJob(job)}
                  invoiceExtraLineItemsAmount={extraLineItemsTotal}
                  invoiceDiscountsAmount={effectiveCalculations.discountsTotal ?? 0}
                  invoiceServiceFeesAmount={serviceFeesTotal}
                  serviceFees={serviceFees}
                  serviceFeeApplyToOptions={discountApplyToOptions}
                  serviceFeeComputedAmounts={serviceFeeComputedAmounts}
                  onAddServiceFee={(draft) =>
                    setServiceFees((prev) => [
                      ...prev,
                      draft
                        ? { ...draft, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` }
                        : {
                            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                            label: globalFeeLabel || "Service fee",
                            kind: "amount",
                            value: "",
                            appliesToType: "invoice",
                            appliesToKey: "invoice",
                          },
                    ])
                  }
                  onRemoveServiceFee={(id) => setServiceFees((prev) => prev.filter((item) => item.id !== id))}
                  onChangeServiceFee={(id, patch) => setServiceFees((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)))}
                  showServiceFeeValidation={hasIncompleteServiceFee}
                  discounts={discounts}
                  discountApplyToOptions={discountApplyToOptions}
                  discountComputedAmounts={discountComputedAmounts}
                  onAddDiscount={(draft) =>
                    setDiscounts((prev) => [
                      ...prev,
                      draft
                        ? { ...draft, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` }
                        : {
                            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                            description: "",
                            kind: "amount",
                            value: "",
                            appliesToType: "invoice",
                            appliesToKey: "invoice",
                          },
                    ])
                  }
                  onRemoveDiscount={(id) => setDiscounts((prev) => prev.filter((item) => item.id !== id))}
                  onChangeDiscount={(id, patch) => setDiscounts((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)))}
                  showDiscountValidation={hasIncompleteDiscount}
                  billedVisitGroups={billedVisitGroups}
                  afterLineItems={
                    <InvoiceExtraLineItemsCard
                      variant="embedded"
                      items={extraLineItems}
                      availableVisits={availableVisits}
                      onAdd={() =>
                        setExtraLineItems((prev) => [
                          ...prev,
                          {
                            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                            title: "",
                            amount: "",
                            unitPrice: "",
                            allocations: availableVisits.length === 1 ? [{ eventUid: availableVisits[0].uid, amount: "" }] : [],
                          },
                        ])
                      }
                      onRemove={handleRemoveExtraLineItem}
                      onChange={(id, field, value) =>
                        setExtraLineItems((prev) =>
                          prev.map((item) =>
                            item.id === id
                              ? {
                                  ...item,
                                  [field]: field === "amount" ? sanitizeAmountDraft(value) : value,
                                }
                              : item,
                          ),
                        )
                      }
                      onChangeItem={(id, updated) => setExtraLineItems((prev) => prev.map((item) => (item.id === id ? updated : item)))}
                      totalAmount={extraLineItemsTotal}
                      showValidation={hasIncompleteExtraLineItem}
                    />
                  }
                />
              ) : null}

              {/* Temporary disabled in UI */}
              {/* <PartialPaymentsCard
                allowPartialPayments={allowPartialPayments}
                onAllowChange={setAllowPartialPayments}
                minimumPaymentAmount={minimumPaymentAmount}
                onAmountChange={(value: string) => setMinimumPaymentAmount(sanitizeAmountDraft(value))}
                cardSx={invoiceEditorSectionCardSx}
                borderSx={invoiceEditorInnerBorder}
              /> */}

              <EmailRecipientsList
                recipients={emailRecipients}
                sendEmail={sendEmail}
                needsPaymentMethod={needsPaymentMethodForCanPay}
                allowCanPay={true}
                onAdd={addRecipient}
                onDelete={deleteRecipient}
                onUpdate={updateRecipient}
                onSendEmailChange={setSendEmail}
              />

              <NotesAndTermsInput value={notes} onChange={updateNotes} />
            </Box>
          </Grid>

          {/* Right Column: Payment Integration & Credit Card Fee */}
          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                position: { xs: "static", lg: "sticky" },
                top: { lg: 24 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <PaymentMethodSelector availablePayments={paymentAvailableData} />
              <PaymentProcessingFeeCard
                serviceFees={serviceFees}
                onServiceFeesChange={setServiceFees}
                computedFeeAmount={(() => {
                  const ccFeeItem = serviceFees.find((f) => f.id === CC_FEE_ID || f.label.toLowerCase().trim() === "credit card fee");
                  return ccFeeItem ? (serviceFeeComputedAmounts[ccFeeItem.id] ?? 0) : 0;
                })()}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Sticky Bottom Action Dock inside Modal */}
      {!clientViewMode && (
        <Box
          sx={{
            position: "sticky",
            bottom: { xs: 16, sm: 20 },
            display: "flex",
            justifyContent: "flex-end",
            pointerEvents: "none",
            zIndex: 10,
            mt: 2,
          }}
        >
          <Button
            sx={{
              ...filledButtonSecondary,
              pointerEvents: "auto",
              minWidth: 160,
              height: 48,
              px: 3.5,
              borderRadius: "999px",
              fontSize: 15,
              fontWeight: 700,
              boxShadow: "0 6px 20px rgba(251, 142, 0, 0.4)",
              "&:hover": {
                backgroundColor: colors.orange[600],
                color: "#FFFFFF",
                boxShadow: "0 8px 24px rgba(251, 142, 0, 0.5)",
              },
            }}
            onClick={handleGenerateInvoice}
            disabled={isGenerateDisabled}
          >
            {isPending ? "Creating..." : "Generate Invoice"}
          </Button>
        </Box>
      )}
      {DialogComponent}
    </Container>
  );
};
